(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.LMLACommerce = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const normalizeId = value => Number(value);

  // Shopify Ajax endpoints must keep the customer's market and language prefix.
  // window.Shopify.routes.root is "/de-de/", "/en-gb/", "/" etc.
  function cartUrl(path) {
    const marketRoot = typeof window !== 'undefined' ? window.Shopify?.routes?.root : null;
    const root = typeof marketRoot === 'string' && marketRoot.startsWith('/')
      ? marketRoot : '/';
    return root.replace(/\/?$/, '/') + String(path).replace(/^\/+/, '');
  }

  function resolveVariant(product, color, size, optionMap) {
    if (!product || !Array.isArray(product.variants)) return null;
    const variantOptions = variant => variant.options || [variant.option1, variant.option2, variant.option3];

    // Prefer the original merchant option keys; they remain stable on the
    // embedded offer selector even when Shopify translates displayed labels.
    const find = (chosenColor, chosenSize) => product.variants.find(variant => {
      const options = variantOptions(variant);
      return options[0] === chosenColor && options[1] === chosenSize && variant.available !== false;
    }) || null;
    const original = find(color, size);
    if (original) return original;

    // A localized product JSON payload can contain translated option values.
    // Map by Shopify's own option order (from product.options_with_values)
    // instead of guessing German, Polish, Swedish, etc. names.
    const colorIndex = optionMap?.canonicalColors?.indexOf(color) ?? -1;
    const sizeIndex = optionMap?.canonicalSizes?.indexOf(size) ?? -1;
    if (colorIndex < 0 || sizeIndex < 0) return null;
    const localizedColor = optionMap?.colors?.[colorIndex];
    const localizedSize = optionMap?.sizes?.[sizeIndex];
    if (typeof localizedColor !== 'string' || typeof localizedSize !== 'string') return null;
    return find(localizedColor, localizedSize);
  }

  function buildBundleItems(product, offerQuantity, selections, groupId, gift, optionMap) {
    const quantity = Number(offerQuantity);
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('A valid offer quantity is required.');
    if (!Array.isArray(selections) || selections.length !== quantity) {
      throw new Error(`${quantity} selections are required for this offer.`);
    }
    const items = selections.map(selection => {
      const variant = resolveVariant(product, selection.color, selection.size, optionMap);
      if (!variant) throw new Error(`${selection.color} / ${selection.size} is not available.`);
      return {
        id: normalizeId(variant.id),
        quantity: 1,
        properties: {
          _lmla_offer: String(quantity),
          _lmla_group: String(groupId),
        },
      };
    });
    if (quantity === 3 && gift) {
      const giftVariantId = normalizeId(gift && gift.variantId);
      if (!giftVariantId || gift.available === false) throw new Error('The free gift card is unavailable.');
      items.push({
        id: giftVariantId,
        quantity: 1,
        properties: {
          _lmla_offer: '3',
          _lmla_group: String(groupId),
          _lmla_gift: gift.title || 'Gift Card',
        },
      });
    }
    return items;
  }

  function quantitiesByVariant(cart) {
    const result = new Map();
    for (const item of cart?.items || []) {
      const id = normalizeId(item.variant_id || item.id);
      result.set(id, (result.get(id) || 0) + Number(item.quantity || 0));
    }
    return result;
  }

  function groupDisplayLines(items) {
    const groups = new Map();
    for (const item of items || []) {
      const properties = item.properties || {};
      const groupId = properties._lmla_group;
      const isGift = Boolean(properties._lmla_gift);
      const mergeKey = groupId && !isGift
        ? `${groupId}|${normalizeId(item.variant_id || item.id)}`
        : `line|${item.key || groups.size}`;
      if (!groups.has(mergeKey)) {
        groups.set(mergeKey, {
          ...item,
          properties,
          quantity: 0,
          original_line_price: 0,
          final_line_price: 0,
          line_level_discount_allocations: [],
          _items: [],
          _keys: [],
        });
      }
      const group = groups.get(mergeKey);
      group.quantity += Number(item.quantity || 0);
      group.original_line_price += Number(item.original_line_price || 0);
      group.final_line_price += Number(item.final_line_price || 0);
      group.line_level_discount_allocations.push(...(item.line_level_discount_allocations || []));
      group._items.push(item);
      if (item.key) group._keys.push(item.key);
    }
    return [...groups.values()].sort((a, b) => Number(Boolean(a.properties?._lmla_gift)) - Number(Boolean(b.properties?._lmla_gift)));
  }

  function giftReconciliation(items, gift) {
    const qualifying = new Map();
    const gifts = new Map();
    for (const item of items || []) {
      const properties = item.properties || {};
      const groupId = properties._lmla_group;
      if (!groupId) continue;
      if (properties._lmla_gift) {
        if (!gifts.has(groupId)) gifts.set(groupId, []);
        gifts.get(groupId).push(item);
      } else {
        qualifying.set(groupId, (qualifying.get(groupId) || 0) + Number(item.quantity || 0));
      }
    }
    const removeKeys = [];
    for (const [groupId, giftItems] of gifts) {
      if (!gift || (qualifying.get(groupId) || 0) < 3) {
        removeKeys.push(...giftItems.map(item => item.key).filter(Boolean));
      } else if (giftItems.length > 1) {
        // Keep exactly one gift card for each qualifying 3-legging group.
        removeKeys.push(...giftItems.slice(1).map(item => item.key).filter(Boolean));
      }
    }
    const addItems = [];
    const giftVariantId = normalizeId(gift && gift.variantId);
    if (giftVariantId && gift?.available !== false) {
      for (const [groupId, quantity] of qualifying) {
        if (quantity >= 3 && !(gifts.get(groupId) || []).length) {
          addItems.push({
            id: giftVariantId,
            quantity: 1,
            properties: { _lmla_offer: '3', _lmla_group: groupId, _lmla_gift: gift.title || 'Gift Card' },
          });
        }
      }
    }
    return { removeKeys, addItems };
  }

  function orphanGiftKeys(items) {
    return giftReconciliation(items, null).removeKeys;
  }

  function cartSignature(cart) {
    return [...quantitiesByVariant(cart).entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([id, quantity]) => `${id}:${quantity}`)
      .join('|');
  }

  function hasAddedItems(beforeCart, afterCart, items) {
    const before = quantitiesByVariant(beforeCart);
    const after = quantitiesByVariant(afterCart);
    const expected = new Map();
    for (const item of items) {
      const id = normalizeId(item.id);
      expected.set(id, (expected.get(id) || 0) + Number(item.quantity || 0));
    }
    return [...expected.entries()].every(([id, quantity]) =>
      (after.get(id) || 0) >= (before.get(id) || 0) + quantity,
    );
  }

  async function readJson(response) {
    let payload = null;
    try { payload = await response.json(); } catch (_) {}
    if (!response.ok) {
      const message = payload?.description || payload?.message || `Shopify returned ${response.status || 'an error'}.`;
      throw new Error(message);
    }
    return payload;
  }

  async function getCart(fetchImpl) {
    const response = await fetchImpl(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } });
    return readJson(response);
  }

  async function addBundle(fetchImpl, items) {
    const beforeCart = await getCart(fetchImpl);
    let postError = null;
    try {
      const response = await fetchImpl(cartUrl('/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      await readJson(response);
    } catch (error) {
      postError = error;
    }

    const cart = await getCart(fetchImpl);
    if (!postError) return { cart, recovered: false };
    if (hasAddedItems(beforeCart, cart, items)) return { cart, recovered: true, warning: postError.message };
    throw new Error(`The add to cart could not be confirmed: ${postError.message}`);
  }

  async function changeLine(fetchImpl, key, quantity) {
    try {
      const response = await fetchImpl(cartUrl('/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) }),
      });
      return await readJson(response);
    } catch (error) {
      try { return await getCart(fetchImpl); }
      catch (_) { throw error; }
    }
  }

  return {
    resolveVariant,
    cartUrl,
    buildBundleItems,
    addBundle,
    changeLine,
    cartSignature,
    groupDisplayLines,
    orphanGiftKeys,
    giftReconciliation,
    getCart,
  };
});
